import { setDimension } from '@propertypal/shared/src/services/analytics';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { NetworkContext } from '@propertypal/shared/src/types/network';
import { Property } from '@propertypal/shared/src/types/property';
import PropertyNotFound from '@propertypal/web-ui/src/property/PropertyNotFound';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useState } from 'react';
import PropertyDetails, { PROPERTY_IMAGE_LOCATOR } from '../components/property/PropertyDetails';
import { setGoogleProperty } from '../services/ads/googletag';
import { requireAuthToken } from '../utils/auth';

interface Props {
  published: boolean;
  property: Property;
}

const getProperty = async (id: string, ctx?: NetworkContext, permit?: string, expiry?: string) => {
  const type = id.includes('d') ? 'development' : 'property';
  const pathId = type === 'development' ? Number(id.replace('d', '')) : Number(id) - 1200;

  let url = `${API_URL}/${type}/${pathId}?imageLocator=${PROPERTY_IMAGE_LOCATOR.join(',')}`;

  // append permit and expiry query params to url if they exist
  if (permit && expiry) url += `&permit=${permit}&expiry=${expiry}`;

  try {
    const result = await axios(
      {
        method: 'GET',
        url,
      },
      undefined,
      ctx,
    );

    return [result.data, true];
  } catch (err: any) {
    if (err.response && err.response.status === 404) {
      return [err.response.data, false];
    }

    throw err;
  }
};

export const getServerSideProps = requireAuthToken(async (ctx) => {
  const id = ctx.query.id as string;
  const address = ctx.query.address as string;
  // check if permit and expiry query strings exists, coming from PDI, property history
  // these allow a user to view a property that has been archived or otherwise removed from site
  const permit = typeof ctx.query.permit === 'string' ? ctx.query.permit : undefined;
  const expiry = typeof ctx.query.expiry === 'string' ? ctx.query.expiry : undefined;

  const [property, published] = await getProperty(id, ctx, permit, expiry);

  if (property.path && (!address || `/${address}/${id}` !== property.path)) {
    const params = ctx.resolvedUrl && ctx.resolvedUrl.split('?')[1];
    const paramsStr = params ? `?${params}` : '';

    return {
      redirect: {
        destination: `${property.path}${paramsStr}`,
        permanent: true,
      },
    };
  }

  ctx.res.statusCode = published ? 200 : 404;

  return {
    props: {
      published,
      property,
    },
  };
});

const PropertyPage: FunctionComponent<Props> = (props) => {
  const router = useRouter();
  const [property, setProperty] = useState<Property>(props.property);

  const updateProperty = async (url: string) => {
    const [, , id] = url.split('/');

    if (id) {
      const [data] = await getProperty(id);
      setProperty(data);
    }
  };

  useEffect(() => {
    // next.js has bug where getServersideProps will not always be called
    // https://github.com/vercel/next.js/issues/34365
    // The following logic checks for a bad route and manually re-fetches the property details
    if (!router.asPath.includes(property.pathId)) {
      updateProperty(router.asPath);
    }

    setDimension(4, property.account?.accountNumber);
  }, [router.asPath]);

  useEffect(() => {
    setProperty(props.property);
  }, [props.property]);

  useEffect(() => {
    setGoogleProperty(property);
  }, [property.id]);

  if (!props.published) {
    return <PropertyNotFound property={property} />;
  }

  return <PropertyDetails key={property.id} property={property} />;
};

export default PropertyPage;
